import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { emailAPI } from "../../../services/api";

const EmailAccountsTab = () => {
  const [loading, setLoading] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [testStatuses, setTestStatuses] = useState({});

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    imap_server: "",
    imap_user: "",
    imap_password: "",
    port: 993,
    active: true,
  });
  const [dialogTestStatus, setDialogTestStatus] = useState(null); // null, 'loading', 'success', 'error'

  useEffect(() => {
    const savedAccounts = localStorage.getItem("emailAccounts");
    if (savedAccounts) {
      try {
        setEmailAccounts(JSON.parse(savedAccounts));
      } catch (e) {
        console.error("Fehler beim Parsen der gespeicherten E-Mail-Konten", e);
      }
    }
    loadEmailAccounts();
  }, []);

  const loadEmailAccounts = async () => {
    try {
      setLoading(true);
      const response = await emailAPI.getEmailAccounts();
      const accounts = response.data || [];
      setEmailAccounts(accounts);
      localStorage.setItem("emailAccounts", JSON.stringify(accounts));

      // Test connections
      testAllAccounts(accounts);
    } catch (err) {
      console.error("Laden der E-Mail-Konten fehlgeschlagen:", err);
    } finally {
      setLoading(false);
    }
  };

  const testAllAccounts = async (accounts) => {
    const newStatuses = {};
    accounts.forEach((acc) => {
      newStatuses[acc.id] = { status: "loading" };
    });
    setTestStatuses((prev) => ({ ...prev, ...newStatuses }));

    accounts.forEach((acc) => {
      testSingleAccount(acc);
    });
  };

  const testSingleAccount = async (account) => {
    try {
      await emailAPI.testEmailAccount(account);
      setTestStatuses((prev) => ({
        ...prev,
        [account.id]: { status: "success", message: "Verbindung erfolgreich" },
      }));
    } catch (err) {
      setTestStatuses((prev) => ({
        ...prev,
        [account.id]: {
          status: "error",
          message: err.message || "Verbindung fehlgeschlagen",
        },
      }));
    }
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        imap_server: account.imap_server,
        imap_user: account.imap_user,
        imap_password: "", // Security: usually empty on edit
        port: account.port,
        active: account.active,
      });
    } else {
      setEditingAccount(null);
      setFormData({
        imap_server: "",
        imap_user: "",
        imap_password: "",
        port: 993,
        active: true,
      });
    }
    setDialogTestStatus(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDialogTest = async () => {
    setDialogTestStatus("loading");
    try {
      await emailAPI.testEmailAccount(formData);
      setDialogTestStatus("success");
    } catch (err) {
      console.error(err);
      setDialogTestStatus("error");
    }
  };

  const handleSave = async () => {
    try {
      if (editingAccount) {
        await emailAPI.updateEmailAccount(editingAccount.id, formData);
      } else {
        await emailAPI.createEmailAccount(formData);
      }
      handleCloseDialog();
      loadEmailAccounts();
    } catch (err) {
      console.error("Speichern fehlgeschlagen:", err);
      alert(
        "Speichern des E-Mail-Kontos fehlgeschlagen: " +
          (err.response?.data?.detail || err.message),
      );
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Sind Sie sicher, dass Sie dieses Konto löschen möchten?")
    ) {
      try {
        await emailAPI.deleteEmailAccount(id);
        loadEmailAccounts();
      } catch (err) {
        console.error("Löschen fehlgeschlagen", err);
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      // Optimistic update
      setEmailAccounts((prev) =>
        prev.map((acc) =>
          acc.id === id ? { ...acc, active: !currentStatus } : acc,
        ),
      );
      await emailAPI.toggleEmailAccountActive(id);
    } catch (err) {
      console.error("Failed to toggle active status", err);
      // Revert on failure
      setEmailAccounts((prev) =>
        prev.map((acc) =>
          acc.id === id ? { ...acc, active: currentStatus } : acc,
        ),
      );
      alert("Statusänderung fehlgeschlagen");
    }
  };

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5">E-Mail-Konten</Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadEmailAccounts}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Neu laden
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            variant="contained"
            color="primary"
            sx={{ color: "white" }}
          >
            Konto hinzufügen
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="textSecondary" mb={2}>
        Die E-Mail-Konten werden über IMAP verbunden. Die erforderlichen Daten
        (Server, Benutzer, Passwort, Port) finden Sie in den Einstellungen Ihres
        E-Mail-Anbieters. Beispiel für Strato:{" "}
        <a
          href="https://www.strato.de/faq/mail/externes-e-mail-programm-mit-strato-e-mail-adresse-nutzen/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "blue", textDecoration: "underline" }}
        >
          Strato IMAP-Einstellungen
        </a>
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Server</TableCell>
              <TableCell>Benutzer</TableCell>
              <TableCell>Port</TableCell>
              <TableCell align="center">Aktiv</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emailAccounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>{account.imap_server}</TableCell>
                <TableCell>{account.imap_user}</TableCell>
                <TableCell>{account.port}</TableCell>
                <TableCell align="center">
                  <Switch
                    checked={account.active}
                    onChange={() =>
                      handleToggleActive(account.id, account.active)
                    }
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {testStatuses[account.id]?.status === "loading" && (
                    <CircularProgress size={20} />
                  )}
                  {testStatuses[account.id]?.status === "success" && (
                    <Tooltip title="Verbindung erfolgreich">
                      <CheckCircleIcon color="success" />
                    </Tooltip>
                  )}
                  {testStatuses[account.id]?.status === "error" && (
                    <Tooltip title={testStatuses[account.id]?.message}>
                      <ErrorIcon color="error" />
                    </Tooltip>
                  )}
                  {!testStatuses[account.id] && <span>-</span>}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleOpenDialog(account)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(account.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {emailAccounts.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Keine E-Mail-Konten gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingAccount
            ? "E-Mail-Konto bearbeiten"
            : "E-Mail-Konto hinzufügen"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="IMAP Server"
              name="imap_server"
              value={formData.imap_server}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="IMAP Benutzer"
              name="imap_user"
              value={formData.imap_user}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="IMAP Passwort"
              name="imap_password"
              type="password"
              value={formData.imap_password}
              onChange={handleChange}
              fullWidth
              placeholder={
                editingAccount ? "Leer lassen, um unverändert zu lassen" : ""
              }
              helperText={
                editingAccount
                  ? "Nur eingeben, wenn Sie das Passwort ändern möchten"
                  : ""
              }
            />
            <TextField
              label="Port"
              name="port"
              type="number"
              value={formData.port}
              onChange={handleChange}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  onChange={handleChange}
                  name="active"
                />
              }
              label="Aktiv"
            />

            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                onClick={handleDialogTest}
                disabled={dialogTestStatus === "loading"}
              >
                Verbindung testen
              </Button>
              {dialogTestStatus === "loading" && <CircularProgress size={20} />}
              {dialogTestStatus === "success" && (
                <Tooltip title="Erfolgreich">
                  <CheckCircleIcon color="success" />
                </Tooltip>
              )}
              {dialogTestStatus === "error" && (
                <Tooltip title="Fehlgeschlagen">
                  <ErrorIcon color="error" />
                </Tooltip>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            sx={{ color: "white" }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailAccountsTab;
